/* Which list a device belongs to.
 *
 * There are three, and until this file existed each screen decided for itself
 * what it held — so a server was in Devices and in Infrastructure, and a printer
 * was in all three. Three lists showing overlapping subsets of one register is
 * worse than one list, because now "where do I look for it" has no answer.
 *
 * The rule is that every device is in exactly one, and the three answer three
 * genuinely different questions:
 *
 *   Devices         which laptop does Rob have
 *   Infrastructure  how do I get into the firewall
 *   Printers        what cartridge does it take
 *
 * The detail is shared. One record, one panel, reached from whichever list found
 * it — because the thing that would make this messy again is three places to
 * edit the same machine.
 */

import type { DeviceType } from '../models';

/** Kit issued to a person. */
export const ENDPOINT_TYPES: DeviceType[] = ['Laptop', 'Desktop', 'Mobile', 'Tablet'];

/** Kit that belongs to the building. Printers are deliberately absent — they
    have their own list, and a device in two lists is what this file is for. */
export const INFRASTRUCTURE_TYPES: DeviceType[] = [
  'Server',
  'Storage',
  'Firewall',
  'Switch',
  'Access point',
  'UPS',
];

export const PRINTER_TYPES: DeviceType[] = ['Printer'];

export type DeviceGroup = 'endpoint' | 'infrastructure' | 'printer';

export function groupOf(type: string): DeviceGroup {
  if (PRINTER_TYPES.includes(type as DeviceType)) return 'printer';
  if (INFRASTRUCTURE_TYPES.includes(type as DeviceType)) return 'infrastructure';
  /* Anything unrecognised counts as an endpoint rather than vanishing. A device
     type added to the model and forgotten here should turn up somewhere a person
     will see it and say "why is that in with the laptops". */
  return 'endpoint';
}

/** Where a device is listed, for a link that should land somewhere useful. */
export function listFor(type: string): string {
  const group = groupOf(type);
  return group === 'printer' ? '/printers' : group === 'infrastructure' ? '/infrastructure' : '/devices';
}

/** Human name for the list, for telling somebody where a record actually lives. */
export function listName(type: string): string {
  const group = groupOf(type);
  return group === 'printer' ? 'Printers' : group === 'infrastructure' ? 'Infrastructure' : 'Devices';
}

/* What a device type actually has.
 *
 * The form learned this and the read panel never did, which is why a printer's
 * record asked for its specification, its operating system and its encryption
 * state — three questions no printer can answer, on the panel somebody opens to
 * find the cartridge code.
 *
 * One definition, used by both. A trait added here appears in the form and on
 * the panel together, which is the only way they stay in step.
 */
export interface DeviceTraits {
  /** A processor, memory, disks and an operating system. */
  spec: boolean;
  /** Intune concepts, and Intune's alone — a switch has neither. */
  managedState: boolean;
  /** Signed out to a person. Infrastructure belongs to the building. */
  assignable: boolean;
  /** A lights-out controller with its own address. */
  lightsOut: boolean;
  printer: boolean;
}

export function traitsFor(type: string): DeviceTraits {
  const group = groupOf(type);
  const spec = ['Laptop', 'Desktop', 'Server', 'Storage', 'Mobile', 'Tablet'].includes(type);
  return {
    spec,
    /* Compliance and encryption come from Intune, which manages endpoints and
       servers. Reporting "Unknown" for a UPS is not missing data, it is a
       question that was never going to have an answer. */
    managedState: spec,
    assignable: group === 'endpoint',
    lightsOut: ['Server', 'Storage'].includes(type),
    printer: group === 'printer',
  };
}
